/**
 * AdminRestoreBundleModal — potwierdzenie przywracania pakietu z soft-delete (T8.2).
 *
 * Backend: POST /api/admin/bundles/:code/restore (no body).
 * Walidacja: 409 BUNDLE_NOT_DELETED, 409 CODE_ALREADY_ACTIVE, 404 BUNDLE_NOT_FOUND.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosError } from 'axios';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { api } from '@/lib/api';

export interface AdminRestoreBundleModalProps {
  bundleCode: string;
  bundleName: string;
  onClose: () => void;
  onSuccess: () => void;
}

export function AdminRestoreBundleModal({
  bundleCode,
  bundleName,
  onClose,
  onSuccess,
}: AdminRestoreBundleModalProps) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onConfirm = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.post(`/admin/bundles/${bundleCode}/restore`);
      onSuccess();
    } catch (err) {
      const axErr = err as AxiosError<{ error?: string }>;
      const code = axErr.response?.data?.error ?? null;
      if (code === 'CODE_ALREADY_ACTIVE') {
        setError(t('admin.bundles.restore.errors.codeTaken'));
      } else if (code === 'BUNDLE_NOT_DELETED') {
        setError(t('admin.bundles.restore.errors.notDeleted'));
      } else {
        setError(t('admin.bundles.restore.errors.generic'));
      }
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => (submitting ? undefined : onClose())}
      title={t('admin.bundles.restore.title', { name: bundleName })}
      description={t('admin.bundles.restore.description')}
      closeOnBackdrop={!submitting}
      closeOnEsc={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            {t('admin.bundles.restore.cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void onConfirm()}
            loading={submitting}
            disabled={submitting}
          >
            {submitting
              ? t('admin.bundles.restore.submitting')
              : t('admin.bundles.restore.confirm')}
          </Button>
        </>
      }
    >
      <p className="text-sm text-text-tertiary">{t('admin.bundles.restore.body')}</p>
      {error && (
        <div
          role="alert"
          className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </Modal>
  );
}
